<template>
  <div class="layout">
    <aside class="sidebar-filters">
      <div>
        <label>Item Name:</label>
        <input v-model="itemFilter" placeholder="Filter Item Name" />
      </div>
      <div>
        <label>Purchase Tag:</label>
        <div class="checkbox-group">
          <label v-for="tag in purchaseTagOptions" :key="tag" class="checkbox-label">
            <input
              type="checkbox"
              :value="tag"
              v-model="purchaseTagFilter"
            />
            {{ tag }}
          </label>
        </div>
      </div>
      <div>
        <label>Status:</label>
        <div class="checkbox-group">
          <label v-for="status in statusOptions" :key="status" class="checkbox-label">
            <input
              type="checkbox"
              :value="status"
              v-model="statusFilter"
            />
            {{ status }}
          </label>
        </div>
      </div>
      <button @click="restoreDefaults" class="restore-btn">Restore Default Filters</button>
    </aside>
    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th>Item Name</th>
            <th>Purchase Tag</th>
            <th>Outstanding</th>
            <th>Stock</th>
            <th>Net Available</th>
            <th>Reorder Level</th>
            <th>Pending POs</th>
            <th>Status</th>
            <th>PO Required</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in filteredRows"
            :key="idx"
            :class="{ 'low-reorder-row': Number(row.F) < 20 }"
          >
            <td>{{ row.A }}</td>
            <td>{{ row.B }}</td>
            <td>{{ row.C }}</td>
            <td>{{ row.D }}</td>
            <td>{{ row.E }}</td>
            <td>{{ row.F }}</td>
            <td>{{ row.G }}</td>
            <td>{{ row.H }}</td>
            <td>{{ row.I }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';

const backendUrl = process.env.VUE_APP_BACKEND_URL;

const tableRows = ref([]);
const itemFilter = ref('');
const purchaseTagFilter = ref(['Purchase Relevant','Bought Out']); // Now an array
const statusFilter = ref(['PO Required']); // Now an array for multi-select
const FILTERS_KEY = 'purchaseReqFilters';
const filteredRows = computed(() =>
  tableRows.value.filter(row =>
    (!itemFilter.value || String(row.A).toLowerCase().includes(itemFilter.value.toLowerCase())) &&
    (!purchaseTagFilter.value.length || purchaseTagFilter.value.includes(row.B)) &&
    (!statusFilter.value.length || statusFilter.value.includes(row.H))
  )
);
const purchaseTagOptions = computed(() => {
  const set = new Set();
  tableRows.value.forEach(row => {
    if (row.B) set.add(row.B);
  });
  return Array.from(set);
});
const statusOptions = computed(() => {
  const set = new Set();
  tableRows.value.forEach(row => {
    if (row.H) set.add(row.H);
  });
  return Array.from(set);
});

function restoreDefaults() {
  itemFilter.value = '';
  purchaseTagFilter.value = ['Purchase Relevant','Bought Out'];
  statusFilter.value = ['PO Required'];
  localStorage.setItem(
    FILTERS_KEY,
    JSON.stringify({
      itemFilter: itemFilter.value,
      purchaseTagFilter: purchaseTagFilter.value,
      statusFilter: statusFilter.value,
    })
  );
}

onMounted(async () => {
  const saved = localStorage.getItem(FILTERS_KEY);
  if (saved) {
    const filters = JSON.parse(saved);
    itemFilter.value = filters.itemFilter ?? '';
    purchaseTagFilter.value = filters.purchaseTagFilter ?? ['Purchase Relevant','Bought Out'];
    statusFilter.value = filters.statusFilter ?? ['PO Required'];
  }
  const response = await fetch(`${backendUrl}/excel/purchase-req`);
  const data = await response.json();
  tableRows.value = (data.items || []).map(row => ({
    A: row[0], B: row[1], C: row[2], D: row[3], E: row[4],
    F: row[5], G: row[6], H: row[7], I: row[8]
  }));
});

// Watch and save filters to localStorage
watch([itemFilter, purchaseTagFilter, statusFilter], () => {
  localStorage.setItem(
    FILTERS_KEY,
    JSON.stringify({
      itemFilter: itemFilter.value,
      purchaseTagFilter: purchaseTagFilter.value,
      statusFilter: statusFilter.value,
    })
  );
}, { deep: true });

</script>

<style>
.table-container {
  overflow-x: auto;
  margin: 2em 0;
}
table {
  border-collapse: collapse;
  width: 100%;
  font-size: 0.85rem;
}
th, td {
  border: 1px solid #ccc;
  padding: 0.5em 0.75em;
  text-align: left;
}
th {
  background: #f4f4f4;
}
.low-reorder-row {
  background-color: #ebb9b9 !important;
  color: #000000;
}
.sidebar-filters {
  min-width: 240px;
  background: #f9fafb;
  padding: 1.5em 1em;
  border-radius: 10px;
  box-shadow: 0 2px 12px #0001;
  display: flex;
  flex-direction: column;
  gap: 1.5em;
  font-family: 'Segoe UI', Arial, sans-serif;
}

.sidebar-filters > div {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}

.sidebar-filters > div > label {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.2em;
  letter-spacing: 0.02em;
}

.sidebar-filters input[type="text"] {
  padding: 0.5em 0.75em;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1em;
  background: #fff;
  transition: border 0.2s;
}

.sidebar-filters input[type="text"]:focus {
  border-color: #3182ce;
  outline: none;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.3em;
  padding-left: 0.5em;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-weight: 400;
  color: #374151;
  cursor: pointer;
  font-size: 0.98em;
}

.checkbox-label input[type="checkbox"] {
  accent-color: #3182ce;
  width: 1.1em;
  height: 1.1em;
}
.restore-btn {
  margin-top: 1em;
  padding: 0.5em 1em;
  background: #3182ce;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s;
}
.restore-btn:hover {
  background: #225ea8;
}
</style>